import type { AuthError } from "@supabase/supabase-js";
import { STRONG_PASSWORD_EXAMPLES } from "@/lib/passwordGuidance";

export type LeakedPasswordResetCta = {
  label: string;
  path: string;
};

export type FriendlyAuthError = {
  title: string;
  description: string;
  /** True when the failure was caused by HIBP / weak-password rejection. */
  isLeakedPassword: boolean;
  /** Strong-password examples to show alongside the warning (leaked case only). */
  examples?: readonly string[];
  /** Optional one-click "reset password" CTA, only when contextually useful. */
  resetCta?: LeakedPasswordResetCta;
};

/**
 * Detect Supabase auth errors caused by the HIBP leaked-password check
 * (or the generic "weak_password" rejection) and return a user-friendly
 * message that guides the user to pick a stronger password or reset it.
 *
 * Supabase surfaces these as either:
 *   - error.code === "weak_password"
 *   - message contains "pwned", "compromised", "leaked", or "data breach"
 */
export function describeAuthError(
  error: Pick<AuthError, "message" | "code"> | { message: string; code?: string } | null,
  opts?: { context?: "signup" | "update" | "reset" },
): FriendlyAuthError {
  const message = error?.message ?? "";
  const code = (error as { code?: string } | null)?.code ?? "";
  const lower = message.toLowerCase();

  const isLeaked =
    code === "weak_password" ||
    lower.includes("pwned") ||
    lower.includes("compromised") ||
    lower.includes("leaked") ||
    lower.includes("data breach") ||
    lower.includes("known to be weak");

  if (isLeaked) {
    const context = opts?.context ?? "signup";
    const guidance =
      context === "signup"
        ? "This password has appeared in a known data breach and can't be used to create an account. Please choose a different password — ideally 12+ characters, unique to Elara Health, and not reused from other sites."
        : "This password has appeared in a known data breach and can't be used here. Please choose a different password — ideally 12+ characters, unique to Elara Health, and not reused from other sites. If you're reusing this password on other accounts, change it there too and consider a password manager.";

    // A "reset via email" CTA only makes sense when the user is already
    // signed in and updating their password inline (Settings). During signup
    // there's no account yet, and during a reset flow they're already there.
    const resetCta: LeakedPasswordResetCta | undefined =
      context === "update"
        ? { label: "Go to reset password", path: "/forgot-password" }
        : undefined;

    return {
      title: "Password found in a data breach",
      description: guidance,
      isLeakedPassword: true,
      examples: STRONG_PASSWORD_EXAMPLES,
      resetCta,
    };
  }

  return {
    title:
      opts?.context === "signup"
        ? "Sign up failed"
        : opts?.context === "reset"
          ? "Couldn't reset password"
          : "Couldn't update password",
    description: message || "Something went wrong. Please try again.",
    isLeakedPassword: false,
  };
}
